/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

const TAG_ID_KEY = /^tagIds(?:\[\d*\])?$/;
const TAG_ID_VALUE = /^[a-f0-9]{32}_[a-z0-9]+$/i;
const MIN_GENRE_PAGE_SIZE = 100;
const TAG_FIELDS = [
  "tagIds",
  "tags",
  "selectedTags",
  "includedTags",
  "includedTagIds",
];

const appendTagIdValues = (value: unknown, into: string[]) => {
  const seen = new Set(into);

  const add = (raw: string) => {
    raw.split(",").forEach((part) => {
      const id = part.trim();
      if (!TAG_ID_VALUE.test(id) || seen.has(id)) {
        return;
      }
      seen.add(id);
      into.push(id);
    });
  };

  const visit = (current: unknown) => {
    if (current == null) {
      return;
    }

    if (typeof current === "string" || typeof current === "number") {
      add(String(current));
      return;
    }

    if (Array.isArray(current)) {
      current.forEach(visit);
      return;
    }

    if (typeof current === "object") {
      const record = current as Record<string, unknown>;
      ["tagId", "id", "referenceId", ...TAG_FIELDS].forEach((key) => {
        const field = record[key];
        if (
          typeof field === "string" ||
          typeof field === "number" ||
          Array.isArray(field)
        ) {
          visit(field);
        }
      });
    }
  };

  visit(value);
};

export const collectGenreTagIds = (
  component?: {
    parameters?: object | null;
    customParameters?: object | null;
    components?: { [key: string]: unknown } | null;
    contentUrl?: { url?: string } | null;
  } | null
): string[] => {
  if (!component) {
    return [];
  }

  const ids: string[] = [];
  [component.parameters, component.customParameters].forEach((source) => {
    if (!source) {
      return;
    }
    const record = source as Record<string, unknown>;
    TAG_FIELDS.forEach((key) => appendTagIdValues(record[key], ids));
  });

  Object.values(component.components || {}).forEach((group) => {
    appendTagIdValues(group, ids);
  });

  const contentQuery = component.contentUrl?.url?.split("?")[1];
  if (contentQuery) {
    const params = new URLSearchParams(contentQuery);
    Array.from(params.keys()).forEach((key) => {
      if (TAG_ID_KEY.test(key)) {
        appendTagIdValues(params.getAll(key), ids);
      }
    });
  }

  return ids;
};

/**
 * RedBee lists selected genres as repeated tag ids. Bracket form (`tagIds[]`)
 * is collapsed to a single value by the OVP proxy, so the rail shows one genre.
 * Repeat plain `tagIds` instead. With no ids, the scheme query returns every genre.
 */
export const normalizeGenreTagUrl = (
  sourceUrl: string,
  extraTagIds: string[] = [],
  pageSize?: number
): string => {
  const [path, query = ""] = sourceUrl.split("?");
  const params = new URLSearchParams(query);
  const tagIds: string[] = [];

  Array.from(params.keys()).forEach((key) => {
    if (!TAG_ID_KEY.test(key)) {
      return;
    }
    appendTagIdValues(params.getAll(key), tagIds);
  });
  appendTagIdValues(extraTagIds, tagIds);

  Array.from(params.keys()).forEach((key) => {
    if (TAG_ID_KEY.test(key)) {
      params.delete(key);
    }
  });

  tagIds.forEach((id) => params.append("tagIds", id));

  const requestedPageSize = Number(params.get("pageSize"));
  const minimumPageSize = Math.max(pageSize || 0, MIN_GENRE_PAGE_SIZE);
  if (!requestedPageSize || requestedPageSize < minimumPageSize) {
    params.set("pageSize", String(minimumPageSize));
  }

  const nextQuery = params.toString();
  return nextQuery ? `${path}?${nextQuery}` : path;
};
